import { Grid, Text, csx } from '@vtex/admin-ui'
import type { ChangeEvent, HTMLAttributes } from 'react'
import React, { useEffect, useState, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  Step,
  StepTitle,
  StepSubmitButton,
  StepInputText,
  StepOrderSummary,
} from '../../../../components'
import { useStep } from '../../../../hooks'
import { formatAddressToExtended } from '../../../../utils'
import type { ClientDataAddress, ClientDataState } from '../../../../typings'

export type AddressComplementStepProps = {
  address: ClientDataAddress
  complement?: string
  isBusy?: boolean
  onStepSubmit?: (
    addressComplement?: ClientDataState['address']['complement']
  ) => void
  onDataChange?: () => void
} & HTMLAttributes<HTMLFormElement>

export const AddressComplementStep = ({
  address,
  complement: initialComplement,
  isBusy,
  onStepSubmit,
  onDataChange,
  ...props
}: AddressComplementStepProps) => {
  const { formatMessage } = useIntl()
  const [complement, setComplement] = useState(initialComplement ?? '')
  const inputComplementRef = useRef<HTMLInputElement>(null)

  const { formRef, handleSubmit, fireFormSubmit } = useStep({
    onSubmit() {
      if (onStepSubmit) {
        onStepSubmit(complement)
      }
    },
  })

  const continueIdMessage = complement
    ? 'continue'
    : 'checkout.home-delivery.step.address-complement.skip-button'

  const handleComplementChange = (event: ChangeEvent<HTMLInputElement>) => {
    setComplement(event.target.value)

    if (onDataChange) {
      onDataChange()
    }
  }

  useEffect(() => {
    inputComplementRef.current?.focus()
  }, [])

  return (
    <Step
      ref={formRef}
      noValidate
      onSubmit={handleSubmit}
      className={csx({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        '@desktop': {
          justifyContent: 'normal',
        },
      })}
      {...props}
    >
      <StepTitle>
        <FormattedMessage id="checkout.home-delivery.step.address-complement.title" />
      </StepTitle>

      <div
        className={csx({
          height: 'full',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        })}
      >
        <div className={csx({ flex: 1 })}>
          <Grid className={csx({ marginBottom: '$space-3', rowGap: '0.5rem' })}>
            <Text
              variant="body"
              tone="secondary"
              className={csx({ display: 'block', marginBottom: '$space-2' })}
            >
              <FormattedMessage id="checkout.confirm-cep-step.label" />
              {` ${address.postalCode}`}
            </Text>
            <Text variant="pageTitle" tone="secondary">
              {formatAddressToExtended({
                ...address,
                complement,
              })}
            </Text>
          </Grid>
          <StepInputText
            ref={inputComplementRef}
            value={complement}
            maxLength={100}
            required
            onChange={handleComplementChange}
            placeholder={formatMessage({
              id: 'checkout.home-delivery.step.address-complement.label',
            })}
            aria-label={formatMessage({
              id: 'checkout.home-delivery.step.address-complement.label',
            })}
          />
        </div>
      </div>

      <StepOrderSummary>
        <StepSubmitButton
          type="submit"
          onClick={fireFormSubmit}
          loading={isBusy}
          disabled={isBusy}
        >
          <FormattedMessage id={continueIdMessage} />
        </StepSubmitButton>
      </StepOrderSummary>
    </Step>
  )
}
